import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle
} from "@/components/ui/dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuSub,
  DropdownMenuSubContent,
  DropdownMenuSubTrigger,
  DropdownMenuTrigger
} from "@/components/ui/dropdown-menu";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue
} from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { useAuthStore } from "@/stores/auth.store";
import { useModelsStore } from "@/stores/models.store";
import {
  ALLOWED_FILE_TYPES,
  MAX_FILE_SIZE,
  MAX_FILES_PER_MESSAGE,
  MAX_TOTAL_FILES_SIZE,
  type ReadChatFileDto,
  type ReadProjectWithRelationsDto
} from "@package/types";
import { useIsMobile } from "@/hooks/use-mobile";
import {
  Check,
  Eye,
  FileText,
  Folder,
  FolderPlus,
  Loader2,
  Paperclip,
  Plus,
  Send,
  X
} from "lucide-react";
import { useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";

interface ChatProjectAssignment {
  projects: ReadProjectWithRelationsDto[];
  currentProjectId?: string | null;
  currentProjectName?: string | null;
  isUpdating?: boolean;
  onChange: (projectId: string | null) => Promise<void> | void;
  onCreateProject?: () => void;
}

interface ChatInputProps {
  onSend: (
    content: string,
    files: File[],
    modelId: string
  ) => Promise<void> | void;
  isSending?: boolean;
  chatFiles?: ReadChatFileDto[];
  placeholder?: string;
  centered?: boolean;
  fullWidth?: boolean;
  projectAssignment?: ChatProjectAssignment;
}

export function ChatInput({
  onSend,
  isSending = false,
  chatFiles = [],
  placeholder,
  centered = false,
  fullWidth = false,
  projectAssignment
}: ChatInputProps) {
  const { t } = useTranslation();
  const models = useModelsStore((s) => s.models);
  const selectedModelId = useModelsStore((s) => s.selectedModelId);
  const setSelectedModelId = useModelsStore((s) => s.setSelectedModelId);
  const user = useAuthStore((s) => s.user);
  const activeModels = models.filter((model) => model.isActive);
  const canSelectModel = user?.role === "MODERATOR" || user?.role === "ADMIN";
  const maxModelId = String(
    activeModels.find((model) => model.displayName === "Max")?.id ?? ""
  );
  const effectiveModelId = canSelectModel ? selectedModelId : maxModelId;
  const isMobile = useIsMobile();

  const [messageInput, setMessageInput] = useState("");
  const [selectedFiles, setSelectedFiles] = useState<File[]>([]);
  const [isFilesDialogOpen, setIsFilesDialogOpen] = useState(false);

  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || []);
    const validFiles = files.filter((file) => {
      if (
        !ALLOWED_FILE_TYPES.includes(
          file.type as (typeof ALLOWED_FILE_TYPES)[number]
        )
      ) {
        toast.error(`${file.name}: ${t("chat.invalidFileType")}`);
        return false;
      }
      if (file.size > MAX_FILE_SIZE) {
        toast.error(`${file.name}: ${t("chat.fileTooLarge")}`);
        return false;
      }
      return true;
    });

    if (selectedFiles.length + validFiles.length > MAX_FILES_PER_MESSAGE) {
      toast.error(t("chat.maxFilesReached"));
      return;
    }

    const totalSize = [...selectedFiles, ...validFiles].reduce(
      (sum, f) => sum + f.size,
      0
    );
    if (totalSize > MAX_TOTAL_FILES_SIZE) {
      toast.error(t("chat.totalFileSizeTooLarge"));
      return;
    }

    setSelectedFiles((prev) => [...prev, ...validFiles]);
    e.target.value = "";
  };

  const removeFile = (index: number) => {
    setSelectedFiles((prev) => prev.filter((_, i) => i !== index));
  };

  const handleSend = async () => {
    if (
      (!messageInput.trim() && selectedFiles.length === 0) ||
      !effectiveModelId
    )
      return;

    const content = messageInput.trim();
    const files = [...selectedFiles];

    setMessageInput("");

    await onSend(content, files, effectiveModelId);
    setSelectedFiles([]);
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const totalFiles = chatFiles.length + selectedFiles.length;
  const currentProject = projectAssignment?.projects.find(
    (project) => project.id === projectAssignment.currentProjectId
  );
  const currentProjectName =
    currentProject?.name ?? projectAssignment?.currentProjectName ?? null;
  const hasProjectAssignment = !!projectAssignment;
  const canChangeProject =
    hasProjectAssignment && !isSending && !projectAssignment.isUpdating;
  const projectMenuItems = projectAssignment ? (
    <>
      {projectAssignment.projects.length === 0 ? (
        <DropdownMenuItem disabled>
          <Folder className="h-4 w-4" />
          {t("projects.noProjects")}
        </DropdownMenuItem>
      ) : (
        projectAssignment.projects.map((project) => (
          <DropdownMenuItem
            key={project.id}
            onClick={() => projectAssignment.onChange(project.id)}
            disabled={
              !canChangeProject ||
              project.id === projectAssignment.currentProjectId
            }
            className="items-start gap-3"
          >
            <Folder className="mt-0.5 h-4 w-4" />
            <span className="min-w-0 flex-1">
              <span className="block truncate">{project.name}</span>
              {project.description ? (
                <span className="block truncate text-xs text-muted-foreground">
                  {project.description}
                </span>
              ) : null}
            </span>
            {project.id === projectAssignment.currentProjectId ? (
              <Check className="mt-0.5 h-4 w-4 text-primary-text" />
            ) : null}
          </DropdownMenuItem>
        ))
      )}

      {currentProjectName ? (
        <>
          <DropdownMenuSeparator />
          <DropdownMenuItem
            onClick={() => projectAssignment.onChange(null)}
            disabled={!canChangeProject}
          >
            <X className="h-4 w-4" />
            {t("projects.removeChat")}
          </DropdownMenuItem>
        </>
      ) : null}

      {projectAssignment.onCreateProject ? (
        <>
          <DropdownMenuSeparator />
          <DropdownMenuItem
            onClick={projectAssignment.onCreateProject}
            disabled={!canChangeProject}
          >
            <FolderPlus className="h-4 w-4" />
            {t("projects.createAndAddChat")}
          </DropdownMenuItem>
        </>
      ) : null}
    </>
  ) : null;

  return (
    <>
      <input
        ref={fileInputRef}
        type="file"
        accept={ALLOWED_FILE_TYPES.join(",")}
        multiple
        className="hidden"
        onChange={handleFileSelect}
      />

      <div
        className={
          fullWidth
            ? "w-full"
            : centered
              ? "w-full max-w-2xl mx-auto"
              : "max-w-3xl mx-auto"
        }
      >
        <div className="flex flex-col rounded-2xl border bg-background p-3 shadow-sm">
          {/* Textarea - oberer Bereich */}
          <Textarea
            value={messageInput}
            onChange={(e) => setMessageInput(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder={placeholder ?? t("chat.placeholder")}
            disabled={isSending}
            className="min-h-[80px] max-h-[200px] resize-none border-0 bg-transparent p-2 shadow-none focus-visible:ring-0"
            rows={3}
          />

          <div className="flex flex-wrap items-center justify-between gap-2 pt-2">
            <div className="flex min-w-0 items-center gap-2">
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    disabled={isSending}
                    title={t("chat.inputActions")}
                    className="relative h-11 w-11 shrink-0 md:h-9 md:w-9"
                  >
                    <Plus className="h-5 w-5" />
                    {totalFiles > 0 && (
                      <span className="absolute -right-1 -top-1 flex h-4 w-4 items-center justify-center rounded-full bg-primary text-[10px] font-medium text-primary-foreground">
                        {totalFiles}
                      </span>
                    )}
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="start" className="w-64">
                  <DropdownMenuItem
                    onClick={() => fileInputRef.current?.click()}
                    disabled={selectedFiles.length >= MAX_FILES_PER_MESSAGE}
                  >
                    <Paperclip className="h-4 w-4" />
                    {t("chat.attachNewFiles")}
                  </DropdownMenuItem>
                  {totalFiles > 0 && (
                    <DropdownMenuItem
                      onClick={() => setIsFilesDialogOpen(true)}
                    >
                      <Eye className="h-4 w-4" />
                      {t("chat.viewAttachedFiles")}
                    </DropdownMenuItem>
                  )}

                  {projectAssignment ? (
                    <>
                      <DropdownMenuSeparator />
                      {isMobile ? (
                        <>
                          <DropdownMenuLabel className="text-xs text-muted-foreground">
                            {currentProjectName
                              ? t("projects.changeChatProject")
                              : t("projects.addChat")}
                          </DropdownMenuLabel>
                          {projectMenuItems}
                        </>
                      ) : (
                        <DropdownMenuSub>
                          <DropdownMenuSubTrigger>
                            <Folder className="h-4 w-4" />
                            {currentProjectName
                              ? t("projects.changeChatProject")
                              : t("projects.addChat")}
                          </DropdownMenuSubTrigger>
                          <DropdownMenuSubContent className="w-72">
                            {projectMenuItems}
                          </DropdownMenuSubContent>
                        </DropdownMenuSub>
                      )}
                    </>
                  ) : null}
                </DropdownMenuContent>
              </DropdownMenu>

              {projectAssignment && currentProjectName ? (
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button
                      type="button"
                      variant="secondary"
                      disabled={!canChangeProject}
                      title={`${t("projects.currentProject")}: ${currentProjectName}`}
                      className="h-11 min-w-0 max-w-[220px] shrink rounded-md bg-primary px-3 text-primary-foreground hover:bg-primary/90 md:h-9"
                    >
                      <Folder className="h-4 w-4 shrink-0" />
                      <span className="truncate">{currentProjectName}</span>
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent
                    align="start"
                    className="w-[calc(100vw-2rem)] sm:w-72"
                  >
                    {projectMenuItems}
                  </DropdownMenuContent>
                </DropdownMenu>
              ) : null}
            </div>

            <div className="flex items-center gap-2">
              {canSelectModel && (
                <Select
                  value={selectedModelId}
                  onValueChange={setSelectedModelId}
                  disabled={isSending}
                >
                  <SelectTrigger className="h-11 w-36 border-0 bg-muted/50 shadow-none focus:ring-0 md:h-9">
                    <SelectValue placeholder={t("chat.selectModel")} />
                  </SelectTrigger>
                  <SelectContent>
                    {activeModels.map((model) => (
                      <SelectItem key={model.id} value={String(model.id)}>
                        {model.displayName}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}

              <Button
                onClick={handleSend}
                disabled={
                  isSending ||
                  (!messageInput.trim() && selectedFiles.length === 0) ||
                  !effectiveModelId
                }
                size="icon"
                className="h-11 w-11 shrink-0 md:h-9 md:w-9"
              >
                {isSending ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <Send className="h-4 w-4" />
                )}
              </Button>
            </div>
          </div>
        </div>
      </div>

      {/* Attached Files Dialog */}
      <Dialog open={isFilesDialogOpen} onOpenChange={setIsFilesDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t("chat.attachedFiles")}</DialogTitle>
          </DialogHeader>
          <div className="space-y-2">
            {chatFiles.length === 0 && selectedFiles.length === 0 ? (
              <p className="text-sm text-muted-foreground">
                {t("chat.noAttachedFiles")}
              </p>
            ) : (
              <>
                {selectedFiles.map((file, index) => (
                  <div
                    key={`pending-${file.name}-${index}`}
                    className="flex items-center gap-2 rounded-md border border-dashed p-3"
                  >
                    <FileText className="h-5 w-5 text-muted-foreground" />
                    <span className="flex-1 truncate text-sm">{file.name}</span>
                    <span className="text-xs text-muted-foreground">
                      {t("chat.pendingUpload")}
                    </span>
                    <button
                      type="button"
                      onClick={() => removeFile(index)}
                      className="text-muted-foreground hover:text-destructive"
                      disabled={isSending}
                    >
                      <X className="h-4 w-4" />
                    </button>
                  </div>
                ))}
                {chatFiles.map((file) => (
                  <div
                    key={file.id}
                    className="flex items-center gap-2 rounded-md border p-3"
                  >
                    <FileText className="h-5 w-5 text-muted-foreground" />
                    <span className="flex-1 truncate text-sm">
                      {file.filename}
                    </span>
                  </div>
                ))}
              </>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
